import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  getFeed,
  createPost,
  uploadFile,
  getMediaUrl,
  getFullProfile,
  getMyAvailability,
} from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import Post from '../components/Post';
import { hasShowcaseContent } from '../utils/showcaseStatus';
import './Feed.css';

const MAX_IMAGES = 5;
const MAX_VIDEOS = 2;
const FEED_PAGE_SIZE = 20;

const fetchFeedPage = async ([, , limit, offset]) => {
  const response = await getFeed(limit, offset);
  return Array.isArray(response.data) ? response.data : [];
};

function getVideoEmbedUrl(url) {
  if (!url) return '';
  // YouTube
  const ytMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  if (ytMatch && ytMatch[2].length === 11) {
    return `https://www.youtube.com/embed/${ytMatch[2]}`;
  }
  // Vimeo
  const vimeoMatch = url.match(
    /(?:vimeo\.com\/(?:video\/|channels\/\w+\/|groups\/\w+\/videos\/|)?)(\d+)/
  );
  if (vimeoMatch && vimeoMatch[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  return url;
}

const numberToWord = (num) => {
  const words = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
    'Twenty',
  ];
  if (num <= 20) return words[num];
  if (num < 100) return num.toString();
  return num.toString();
};

function Feed() {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { showError } = useNotification();

  // Media attachments
  const [mediaItems, setMediaItems] = useState([]);
  const [showMediaInput, setShowMediaInput] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const feedEndRef = useRef(null);

  const {
    data: feedPages,
    error: feedError,
    mutate: refreshFeed,
    size,
    setSize,
    isValidating: feedValidating,
  } = useSWRInfinite((pageIndex, previousPageData) => {
    if (!user) return null;
    if (previousPageData && previousPageData.length === 0) return null;
    // SHO-19: include user.id in the key so feed data from a previous
    // session isn't served to a different account on the same device.
    // Without this, the cached posts (with their stale Connect/Pending
    // state for the prior viewer) flash before the revalidation fetches
    // the new viewer's relationships.
    return ['feed', user.id, FEED_PAGE_SIZE, pageIndex * FEED_PAGE_SIZE];
  }, fetchFeedPage);

  const posts = useMemo(() => (feedPages || []).flat(), [feedPages]);
  const latestPage = feedPages?.[feedPages.length - 1];
  const hasMore = !latestPage || latestPage.length >= FEED_PAGE_SIZE;
  const loadingMore = feedValidating && size > 1;

  const refreshPosts = useCallback(() => {
    refreshFeed();
  }, [refreshFeed]);

  const { data: timelineData } = useSWR(user ? ['availability', 'me'] : null, async () => {
    const res = await getMyAvailability();
    return res.data;
  });

  const timelineBlocks = timelineData?.blocks || [];

  const { data: fullProfile } = useSWR(user?.id ? ['full-profile', user.id] : null, async () => {
    const res = await getFullProfile(user.id);
    return res.data;
  });

  useEffect(() => {
    if (!feedError) return;
    console.error('Error loading feed:', feedError);
    showError('Failed to load feed. Please try again.');
  }, [feedError, showError]);

  // SHO-51: banner is shown iff the user hasn't saved any showcase content
  // yet. Same predicate as the Profile page's "Set up your Showcase" card,
  // so the two never disagree. No dismiss button — per design, users must
  // know where the showcase page lives, and the banner self-removes the
  // moment they save anything.
  const showShowcaseAlert = fullProfile ? !hasShowcaseContent(fullProfile) : false;

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setSize(size + 1);
  }, [hasMore, loadingMore, setSize, size]);

  // Infinite scroll observer
  useEffect(() => {
    if (!feedEndRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(feedEndRef.current);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  const imageCount = mediaItems.filter((m) => m.media_type === 'image').length;
  const videoCount = mediaItems.filter((m) => m.media_type === 'video').length;
  const canAddImage = imageCount < MAX_IMAGES;
  const canAddVideo = videoCount < MAX_VIDEOS;

  const handleAddMedia = () => {
    if (!mediaUrl.trim()) return;

    if (mediaType === 'image' && !canAddImage) return;
    if (mediaType === 'video' && !canAddVideo) return;

    const newItem = {
      media_type: mediaType,
      url: mediaUrl.trim(),
      embed_url: mediaType === 'video' ? getVideoEmbedUrl(mediaUrl.trim()) : null,
    };

    setMediaItems([...mediaItems, newItem]);
    setMediaUrl('');
    setShowMediaInput(false);
  };

  const handleRemoveMedia = (index) => {
    setMediaItems(mediaItems.filter((_, i) => i !== index));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const isVideo = file.type.startsWith('video/');
        if (isVideo && videoCount >= MAX_VIDEOS) continue;
        if (!isVideo && imageCount >= MAX_IMAGES) continue;

        const response = await uploadFile(file);
        const url = response.data?.url;
        setMediaItems((prev) => [
          ...prev,
          {
            media_type: isVideo ? 'video' : 'image',
            url,
          },
        ]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showError(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!content.trim() && mediaItems.length === 0) return;

    setLoading(true);
    try {
      await createPost({
        content,
        post_type: postType,
        media: mediaItems.length > 0 ? mediaItems : undefined,
      });
      setContent('');
      setPostType('update');
      setMediaItems([]);
      setIsModalOpen(false);
      refreshPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      showError(error.response?.data?.error || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    if (user) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setContent('');
    setPostType('update');
    setMediaItems([]);
    setShowMediaInput(false);
    setMediaUrl('');
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getInitial = () => {
    if (user?.display_name) return user.display_name[0].toUpperCase();
    if (user?.username) return user.username[0].toUpperCase();
    return '?';
  };

  return (
    <div className="feed-layout">
      {/* Left Sidebar - Brand Card */}
      <div className="feed-sidebar-left">
        <div className="brand-card">
          <div className="brand-card-content">
            <p className="brand-card-slogan">
              Your Network.
              <br />
              Your Spotlight.
            </p>
          </div>
          <div className="brand-card-accent"></div>
        </div>

        {/* Suggested Jobs Card */}
        <div className="jobs-card">
          <div className="jobs-card-header">
            <span className="jobs-card-title">Your Suggested Jobs.</span>
          </div>
          <div className="jobs-card-coming-soon">
            <span>Coming Soon</span>
          </div>
        </div>

        {/* Trending */}
        <div className="trending-card">
          <div className="trending-card-header">
            <span className="trending-card-title">Trending.</span>
          </div>
          <div className="trending-card-list">
            {posts
              .filter((p) => p.like_count > 0 || p.comment_count > 0)
              .sort((a, b) => b.like_count + b.comment_count - (a.like_count + a.comment_count))
              .slice(0, 4)
              .map((p) => (
                <div
                  key={p.id}
                  className="trending-card-item"
                  onClick={() => {
                    const el = document.getElementById(`post-${p.id}`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      el.style.transition = 'box-shadow 0.3s';
                      el.style.boxShadow = '0 0 0 3px rgba(90, 64, 125, 0.3)';
                      setTimeout(() => {
                        el.style.boxShadow = '';
                      }, 2000);
                    }
                  }}
                >
                  <div className="trending-card-item-info">
                    <span className="trending-card-item-author">{p.full_name || p.username}</span>
                    <span className="trending-card-item-preview">
                      {p.content?.substring(0, 60)}
                      {p.content?.length > 60 ? '...' : ''}
                    </span>
                  </div>
                  <span className="trending-card-item-stats">
                    {p.like_count > 0 && `${p.like_count} ♥`}
                    {p.like_count > 0 && p.comment_count > 0 && ' · '}
                    {p.comment_count > 0 && `${p.comment_count} 💬`}
                  </span>
                </div>
              ))}
            {posts.filter((p) => p.like_count > 0 || p.comment_count > 0).length === 0 && (
              <p className="trending-card-empty">No trending posts yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Feed Column */}
      <div className="feed-main">
        {showShowcaseAlert && (
          <div className="profile-completion-alert">
            <div className="profile-alert-content">
              <div className="profile-alert-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
              </div>
              <div>
                <strong>Complete your showcase page</strong>
                <p>
                  Add your professions, credits, bio, or photos to get better connection suggestions
                  and let people find you.
                </p>
              </div>
            </div>
            <div className="profile-alert-actions">
              <Link to="/profile-settings" className="profile-alert-btn">
                Set Up Showcase
              </Link>
            </div>
          </div>
        )}
        <div className="posts-list">
          {posts.length === 0 ? (
            <div className="empty-state">
              <svg
                className="empty-state-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <h3>No posts yet</h3>
              <p>Follow some users or create your first post to get started!</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <div key={post.id} id={`post-${post.id}`}>
                  <Post post={post} onDelete={refreshPosts} onUpdate={refreshPosts} />
                </div>
              ))}
              {hasMore && (
                <div ref={feedEndRef} className="feed-load-more">
                  {loadingMore && <span>Loading more...</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="feed-sidebar">
        <div className="you-card">
          <div className="you-card-header">
            <span className="you-card-title">You.</span>
          </div>
          <div className="you-card-stats">
            <div className="you-card-stat-row">
              <span className="you-card-stat-number">
                {numberToWord(posts.filter((p) => p.user_id === user?.id).length || 0)}
              </span>
              <span className="you-card-stat-label">
                {posts.filter((p) => p.user_id === user?.id).length === 1 ? 'post.' : 'posts.'}
              </span>
            </div>
            <div className="you-card-stat-row">
              <span className="you-card-stat-number">
                {numberToWord(fullProfile?.following_count || 0)}
              </span>
              {/* SHO-29: "in your network" was reading from a stale, never-set
                  user.followers_count and conflated direction. Per spec, the
                  count we want here is "people you follow." */}
              <span className="you-card-stat-label">following.</span>
            </div>
            <div className="you-card-stat-row">
              <span className="you-card-stat-number">
                {numberToWord(fullProfile?.follower_count || 0)}
              </span>
              <span className="you-card-stat-label">
                {(fullProfile?.follower_count || 0) === 1 ? 'follower.' : 'followers.'}
              </span>
            </div>
          </div>
          <div className="you-card-actions">
            <button className="you-card-btn" onClick={openModal} disabled={!user}>
              New Post
            </button>
            <Link to={`/profile/${user?.id}`} className="you-card-btn secondary">
              View Profile
            </Link>
          </div>
          <div className="you-card-accent"></div>
        </div>

        {/* Timeline Card */}
        <div className="timeline-card">
          <div className="timeline-card-header">
            <span className="timeline-card-title">Your Timeline.</span>
          </div>
          <div className="timeline-card-list">
            {timelineBlocks.length > 0 ? (
              timelineBlocks.map((block) => {
                const start = new Date(block.start_date);
                const dateStr = start.toLocaleDateString([], { month: 'short', day: 'numeric' });
                return (
                  <div key={block.id} className="timeline-card-item">
                    <span className="timeline-card-date">{dateStr}</span>
                    <span className="timeline-card-event">
                      {block.title || (block.block_type === 'booked' ? 'Booked' : 'Auditioning')}
                      {block.company && ` · ${block.company}`}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="timeline-card-empty">
                <span>No upcoming schedule</span>
                <Link to={`/profile/${user?.id}`} className="timeline-card-add">
                  Add to your timeline
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create a post</h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close post composer">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="18"
                  height="18"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreatePost}>
              <div className="modal-body">
                <div className="modal-user-row">
                  <div className="modal-avatar">
                    {user?.profile_image ? (
                      <img src={getMediaUrl(user.profile_image)} alt="" />
                    ) : (
                      getInitial()
                    )}
                  </div>
                  <div className="modal-user-detail">
                    <span className="modal-username">{user?.full_name || user?.username}</span>
                    <div className="post-type-selector">
                      {[
                        { id: 'update', label: 'Post' },
                        { id: 'audition', label: 'Audition' },
                        { id: 'show', label: 'Show' },
                        { id: 'announcement', label: 'News' },
                      ].map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className={`post-type-chip ${postType === t.id ? 'active' : ''}`}
                          onClick={() => setPostType(t.id)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <textarea
                  className="modal-textarea"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={
                    postType === 'audition'
                      ? 'What are you casting? Include the show, roles, dates, and location...'
                      : postType === 'show'
                        ? 'Tell people about your show — dates, venue, how to get tickets...'
                        : postType === 'announcement'
                          ? 'Share your news with your network...'
                          : "What's on your mind?"
                  }
                  rows="4"
                  autoFocus
                />
              </div>

              {/* Attached Media Preview */}
              {mediaItems.length > 0 && (
                <div className="media-attachments">
                  {mediaItems.map((item, index) => (
                    <div key={index} className="media-attachment-item">
                      {item.media_type === 'image' ? (
                        <div className="media-attachment-preview">
                          <img
                            src={item.url}
                            alt={`Attachment ${index + 1}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="media-attachment-fallback" style={{ display: 'none' }}>
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              width="20"
                              height="20"
                            >
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                            <span>Image</span>
                          </div>
                        </div>
                      ) : (
                        <div className="media-attachment-preview video">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="20"
                            height="20"
                          >
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                          <span>Video</span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="media-attachment-remove"
                        onClick={() => handleRemoveMedia(index)}
                        aria-label={`Remove attachment ${index + 1}`}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          width="14"
                          height="14"
                        >
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Media Input */}
              {showMediaInput && (
                <div className="modal-video-input">
                  <div className="modal-video-input-header">
                    <span>Paste a YouTube or Vimeo link</span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMediaInput(false);
                        setMediaUrl('');
                      }}
                      aria-label="Close video link input"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                  <div className="modal-video-input-row">
                    <input
                      type="text"
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMedia())}
                      autoFocus
                    />
                    <button type="button" onClick={handleAddMedia} disabled={!mediaUrl.trim()}>
                      Embed
                    </button>
                  </div>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                multiple
                style={{ display: 'none' }}
                aria-label="Upload media"
              />

              <div className="modal-footer">
                <div className="modal-footer-left">
                  <button
                    type="button"
                    className="modal-media-icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    title="Upload photo"
                    aria-label="Upload photo"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="modal-media-icon"
                    onClick={() => {
                      setMediaType('video');
                      setShowMediaInput(true);
                    }}
                    title="Add video link"
                    aria-label="Add video link"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <polygon points="23 7 16 12 23 17 23 7" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                  </button>
                </div>
                <button
                  type="submit"
                  className="modal-post-btn"
                  disabled={loading || uploading || (!content.trim() && mediaItems.length === 0)}
                >
                  {loading ? 'Posting...' : uploading ? 'Uploading...' : 'Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Feed;
